import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminAPI, productAPI } from '../../config/api';
import {
  BOOKING_STATUSES,
  getCategoryName,
  getUploadUrl,
  productPayload,
  productToForm,
} from '../../utils/adminFields';
import resolveImageUrl from '../../utils/resolveImageUrl';
import swal from 'sweetalert';
import AdminSidebar from '../../components/AdminSidebar';
import { showAppToast } from '../../utils/notifications';

function EditProduct({ products, categories, onUpdate }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [formData, setFormData] = useState(() => productToForm(null));
  const [errors, setErrors] = useState({});
  const product = products?.find((item) => String(item.id) === String(id));
  const editorCategory = categories?.find((category) => String(category.id) === String(formData.categoryId));
  const editorCategoryName = editorCategory ? getCategoryName(editorCategory) : product?.categoryName || 'Fleet listing';
  const previewImage = formData.imageURL ? resolveImageUrl(formData.imageURL) : '';
  const filledFeatureCount = formData.features.filter((item) => item.trim()).length;
  const filledImageCount = formData.carousel_imgs.filter((item) => item.trim()).length;

  useEffect(() => {
    if (product) setFormData(productToForm(product));
  }, [product]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: '' }));
  };

  const updateArrayField = (field, index, value) => {
    setFormData((current) => ({
      ...current,
      [field]: current[field].map((item, itemIndex) => (itemIndex === index ? value : item)),
    }));
  };

  const addArrayItem = (field, max) => {
    setFormData((current) => (
      current[field].length >= max ? current : { ...current, [field]: [...current[field], ''] }
    ));
  };

  const removeArrayItem = (field, index, min) => {
    setFormData((current) => (
      current[field].length <= min
        ? current
        : { ...current, [field]: current[field].filter((_, itemIndex) => itemIndex !== index) }
    ));
  };

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !uploadTarget) return;

    const imageForm = new FormData();
    imageForm.append('file', file);
    setLoading(true);
    try {
      const response = await adminAPI.uploadImage(imageForm);
      const url = getUploadUrl(response);
      if (!url) throw new Error('Upload did not return an image URL');

      if (uploadTarget.type === 'main') {
        setFormData((current) => ({ ...current, imageURL: url }));
      } else {
        updateArrayField('carousel_imgs', uploadTarget.index, url);
      }
      showAppToast({ message: 'Image uploaded', type: 'success', duration: 2200 });
    } catch (error) {
      showAppToast({ message: error.response?.data?.message || error.message || 'Upload failed', type: 'error' });
    } finally {
      setLoading(false);
      setUploadTarget(null);
      event.target.value = '';
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.categoryId) nextErrors.categoryId = 'Category is required';
    if (!formData.name.trim()) nextErrors.name = 'Vehicle name is required';
    if (!formData.description.trim()) nextErrors.description = 'Description is required';
    if (!formData.imageURL.trim()) nextErrors.imageURL = 'Image URL is required';
    if (!formData.price || Number(formData.price) <= 0) nextErrors.price = 'Valid price is required';
    if (!formData.bookingStatus) nextErrors.bookingStatus = 'Booking status is required';
    if (!formData.features.some((item) => item.trim())) nextErrors.features = 'At least one feature is required';
    if (!formData.carousel_imgs.some((item) => item.trim())) nextErrors.carousel_imgs = 'At least one vehicle image is required';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      showAppToast({ message: 'Please fill in all required fields', type: 'warning' });
      return;
    }

    setLoading(true);
    try {
      await productAPI.update(id, productPayload(formData));
      showAppToast({ message: 'Vehicle updated successfully', type: 'success', duration: 2400 });
      if (onUpdate) onUpdate();
      navigate('/admin/vehicles');
    } catch (error) {
      showAppToast({ message: error.response?.data?.message || 'Failed to update vehicle', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const result = await swal({
      title: 'Delete Vehicle?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      buttons: ['Cancel', 'Delete'],
      dangerMode: true,
    });

    if (!result) return;

    setLoading(true);
    try {
      await productAPI.delete(id);
      showAppToast({ message: 'Vehicle deleted successfully', type: 'success', duration: 2400 });
      if (onUpdate) onUpdate();
      navigate('/admin/vehicles');
    } catch (error) {
      showAppToast({ message: error.response?.data?.message || 'Failed to delete vehicle', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!product) {
    return (
      <div className="admin-layout">
        <AdminSidebar />
        <div className="admin-content px-4 py-16 text-center">
          <h1 className="section-title text-3xl text-primary-theme">Vehicle Not Found</h1>
          <button type="button" onClick={() => navigate('/admin/vehicles')} className="btn-luxury mt-6">
            <i className="bi bi-arrow-left" aria-hidden="true" />
            Back to Vehicles
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content px-4 py-8">
      <div className="mx-auto max-w-6xl admin-vehicle-editor-page">
        <section className="admin-editor-hero card-luxury">
          <div className="admin-editor-hero-copy">
            <span className="admin-editor-eyebrow">
              <i className="bi bi-car-front-fill" aria-hidden="true" />
              Fleet editor
            </span>
            <h1 className="section-title text-4xl text-primary-theme">Edit Vehicle</h1>
            <p className="text-secondary-theme">
              Refine pricing, status, images, and highlights for {formData.name || product.name}.
            </p>
            <div className="admin-editor-hero-stats" aria-label="Vehicle edit summary">
              <span>
                <i className="bi bi-tags" aria-hidden="true" />
                {editorCategoryName}
              </span>
              <span>
                <i className="bi bi-cash-stack" aria-hidden="true" />
                {formData.price ? `US$${formData.price}/day` : 'Rate pending'}
              </span>
              <span>
                <i className="bi bi-images" aria-hidden="true" />
                {filledImageCount} image{filledImageCount === 1 ? '' : 's'}
              </span>
              <span>
                <i className="bi bi-stars" aria-hidden="true" />
                {filledFeatureCount} feature{filledFeatureCount === 1 ? '' : 's'}
              </span>
            </div>
          </div>
          <div className="admin-editor-preview-card" aria-label="Vehicle preview image">
            {previewImage ? (
              <img src={previewImage} alt={formData.name || product.name} />
            ) : (
              <i className="bi bi-image" aria-hidden="true" />
            )}
          </div>
        </section>

        <form onSubmit={handleSubmit} className="admin-form-card card-luxury admin-vehicle-editor-card">
          <div className="admin-form-grid">
            <label className="admin-field">
              <span><i className="bi bi-tags" aria-hidden="true" /> Category</span>
              <select name="categoryId" value={formData.categoryId} onChange={handleChange} required>
                <option value="">Select Category</option>
                {categories?.map((category) => (
                  <option key={category.id} value={category.id}>
                    {getCategoryName(category)}
                  </option>
                ))}
              </select>
              {errors.categoryId && <small>{errors.categoryId}</small>}
            </label>

            <label className="admin-field">
              <span><i className="bi bi-car-front" aria-hidden="true" /> Name</span>
              <input type="text" name="name" value={formData.name} onChange={handleChange} required />
              {errors.name && <small>{errors.name}</small>}
            </label>

            <label className="admin-field">
              <span><i className="bi bi-cash-stack" aria-hidden="true" /> Price</span>
              <input type="number" name="price" value={formData.price} onChange={handleChange} min="0" step="0.01" required />
              {errors.price && <small>{errors.price}</small>}
            </label>

            <label className="admin-field">
              <span><i className="bi bi-clipboard-check" aria-hidden="true" /> Booking Status</span>
              <select name="bookingStatus" value={formData.bookingStatus} onChange={handleChange} required>
                {BOOKING_STATUSES.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              {errors.bookingStatus && <small>{errors.bookingStatus}</small>}
            </label>
          </div>

          <label className="admin-field mt-4">
            <span><i className="bi bi-image" aria-hidden="true" /> Image URL</span>
            <div className="admin-inline-input">
              <input type="text" name="imageURL" value={formData.imageURL} onChange={handleChange} required />
              <label className="btn-luxury-outline cursor-pointer admin-upload-button">
                <i className="bi bi-cloud-arrow-up" aria-hidden="true" />
                Upload
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onClick={() => setUploadTarget({ type: 'main' })}
                  onChange={handleUpload}
                />
              </label>
            </div>
            {errors.imageURL && <small>{errors.imageURL}</small>}
          </label>

          {formData.imageURL && (
            <img src={resolveImageUrl(formData.imageURL)} alt="Vehicle preview" className="admin-preview-image" />
          )}

          <label className="admin-field mt-4">
            <span><i className="bi bi-card-text" aria-hidden="true" /> Description</span>
            <textarea name="description" value={formData.description} onChange={handleChange} rows={4} required />
            {errors.description && <small>{errors.description}</small>}
          </label>

          <div className="admin-array-section">
            <div className="admin-editor-section-header">
              <h2 className="section-title text-2xl text-primary-theme">Features</h2>
              <button type="button" onClick={() => addArrayItem('features', 6)} className="btn-luxury-outline">
                <i className="bi bi-plus-lg" aria-hidden="true" />
                Add Feature
              </button>
            </div>
            {formData.features.map((feature, index) => (
              <div key={`feature-${index}`} className="admin-array-row admin-array-row--action">
                <input
                  type="text"
                  value={feature}
                  onChange={(event) => updateArrayField('features', index, event.target.value)}
                  placeholder="Enter a feature"
                />
                <button
                  type="button"
                  onClick={() => removeArrayItem('features', index, 3)}
                  className="admin-icon-action admin-icon-action--danger"
                  aria-label={`Remove feature ${index + 1}`}
                  title={`Remove feature ${index + 1}`}
                >
                  <i className="bi bi-trash3-fill" aria-hidden="true" />
                </button>
              </div>
            ))}
            {errors.features && <small className="admin-error">{errors.features}</small>}
          </div>

          <div className="admin-array-section">
            <div className="admin-editor-section-header">
              <h2 className="section-title text-2xl text-primary-theme">Vehicle Images</h2>
              <button type="button" onClick={() => addArrayItem('carousel_imgs', 12)} className="btn-luxury-outline">
                <i className="bi bi-plus-lg" aria-hidden="true" />
                Add Image
              </button>
            </div>
            {formData.carousel_imgs.map((image, index) => (
              <div key={`image-${index}`} className="admin-array-row admin-array-row--media">
                <input
                  type="text"
                  value={image}
                  onChange={(event) => updateArrayField('carousel_imgs', index, event.target.value)}
                  placeholder="Enter an image URL"
                />
                <label
                  className="admin-icon-action admin-icon-action--upload"
                  aria-label={`Upload vehicle image ${index + 1}`}
                  title={`Upload vehicle image ${index + 1}`}
                >
                  <i className="bi bi-cloud-arrow-up-fill" aria-hidden="true" />
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onClick={() => setUploadTarget({ type: 'carousel', index })}
                    onChange={handleUpload}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => removeArrayItem('carousel_imgs', index, 4)}
                  className="admin-icon-action admin-icon-action--danger"
                  aria-label={`Remove vehicle image ${index + 1}`}
                  title={`Remove vehicle image ${index + 1}`}
                >
                  <i className="bi bi-trash3-fill" aria-hidden="true" />
                </button>
              </div>
            ))}
            {errors.carousel_imgs && <small className="admin-error">{errors.carousel_imgs}</small>}
          </div>

          <div className="admin-editor-actions">
            <button type="submit" disabled={loading} className="btn-luxury">
              <i className={`bi ${loading ? 'bi-hourglass-split' : 'bi-check2-circle'}`} aria-hidden="true" />
              {loading ? 'Saving...' : 'Update Vehicle'}
            </button>
            <button type="button" onClick={handleDelete} disabled={loading} className="admin-danger-button">
              <i className="bi bi-trash3-fill" aria-hidden="true" />
              Delete
            </button>
            <button type="button" onClick={() => navigate('/admin/vehicles')} disabled={loading} className="btn-luxury-outline">
              <i className="bi bi-x-lg" aria-hidden="true" />
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
    </div>

  );
}

export default EditProduct;
